import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { friendlyErrorMessage } from '../../../shared/friendlyError';
import { platformOperationsGateway } from '../PlatformOperationsGateway';
import type { PlatformMetricDefinition, PlatformOperationsResponse } from '../types';
import { BarList, MetricInfo, PlatformSection, Stat, StatusFromServer, StatusPill, Unavailable } from '../components/PlatformUi';
import '../platformCenter.css';

/**
 * The Super Admin Operations and Risk Center, at /super-admin. A platform-wide view for authorized Escochex staff, reserved to a caller holding
 * `platform.dashboard.read` (SuperAdmin): the SERVER enforces that (token-excluded permission, fails closed), and a caller without it gets a real 403 shown below as
 * a restricted state. There is no client-side role check, and hiding a section would not be an authorization control.
 *
 * It is informational and navigational only. Nothing here starts, retries, settles or moves money, or changes a company's data.
 *
 * Every section comes from the module that owns the data and is independently available: when one module is down only its section says "Data unavailable" (with the
 * reason), and the rest keep working. A missing figure is never shown as zero and never estimated. Figures that EPN has no source for (pilot or suspended status,
 * monthly recurring revenue, reconciliation across modules, backup confirmation, error rates) are listed at the end with their reasons. Company-level rows need the
 * separate `platform.companies.drilldown` permission, are audited with the company ids shown, and never include employee, compensation or bank data.
 *
 * The platform dashboard and the company dashboard share no figures, permissions or data contracts; this page keeps its own small presentation components
 * (PlatformUi.tsx) until the shared dashboard primitives land on main.
 */
export function SuperAdminDashboardPage() {
  const [data, setData] = useState<PlatformOperationsResponse | null>(null);
  const [metrics, setMetrics] = useState<PlatformMetricDefinition[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await platformOperationsGateway.getOperations());
    } catch (err) {
      setError(friendlyErrorMessage(err, 'Could not load the Super Admin Dashboard.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void load();
  }, [load]);

  useEffect(() => {
    // The explanations are an aid, not part of the figures: if they cannot load the page still shows every number.
    const controller = new AbortController();
    platformOperationsGateway.getMetrics(controller.signal).then((m) => { if (!controller.signal.aborted) setMetrics(m); }).catch(() => undefined);
    return () => controller.abort();
  }, []);

  const byId = useMemo(() => new Map(metrics.map((m) => [m.metricId, m])), [metrics]);

  return (
    <section className="card card--wide platform-center">
      <div className="section-header">
        <h2>Super Admin Dashboard</h2>
      </div>

      {error && (
        <div className="pc-card" data-testid="super-admin-dashboard-error" role="alert">
          <p>{error}</p>
          <button type="button" onClick={() => void load()}>Retry</button>
        </div>
      )}

      {loading && <p className="pc-muted" data-testid="super-admin-dashboard-loading">Loading…</p>}

      {!loading && data && <CenterContent data={data} info={(id) => <MetricInfo metric={byId.get(id)} />} />}
    </section>
  );
}

function formatInstant(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime())
    ? iso
    : new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short' }).format(date);
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return iso;
  return ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][m - 1] + ` ${d}, ${y}`;
}

const money = (n: number) => n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });

function CenterContent({ data, info }: { data: PlatformOperationsResponse; info: (metricId: string) => ReactNode }) {
  const s = data.sections;
  const unavailableById = new Map(data.unavailableMetrics.map((m) => [m.metricId, m]));
  const gap = (id: string) => unavailableById.get(id);

  return (
    <>
      <header className="pc-header" data-testid="platform-center-header">
        <p className="pc-header__title">Platform operations and risk center</p>
        <p className="pc-note">
          Informational only: nothing on this page moves money or changes a company's data. Every section is read from the service that owns it, and a
          figure that cannot be supplied is shown as unavailable, never as zero.
        </p>
        <p className="pc-muted">Data as of <time dateTime={data.generatedAtUtc}>{formatInstant(data.generatedAtUtc)}</time> · US dollars · times in UTC</p>
      </header>

      <PlatformSection id="portfolio" title="Portfolio" status={s.portfolio} info={info('active-client-companies')}>
        {data.portfolio && (
          <dl className="pc-stats" data-testid="super-admin-dashboard-kpis">
            <Stat label="Total Companies" value={data.portfolio.totalCompanies} />
            <Stat label="Active Companies" value={data.portfolio.activeCompanies} />
            <Stat label="Inactive Companies" value={data.portfolio.inactiveCompanies} />
          </dl>
        )}
        <ul className="pc-gaps" aria-label="Portfolio figures EPN cannot supply">
          {['pilot-companies', 'suspended-companies'].map((id) => gap(id) && (
            <li key={id}><strong>{gap(id)!.displayName}:</strong> data unavailable. {gap(id)!.reason}</li>
          ))}
        </ul>
      </PlatformSection>

      <PlatformSection id="payroll" title="Payroll operations" status={s.payroll} info={info('payroll-runs-by-status')}>
        {data.payroll && (
          <>
            <dl className="pc-stats">
              <Stat label="Companies awaiting approval" value={data.payroll.companiesAwaitingApproval} info={info('runs-awaiting-approval')} emphasis={data.payroll.companiesAwaitingApproval > 0 ? 'attention' : undefined} />
              <Stat label="Not approved, check date within 7 days" value={data.payroll.notApprovedWithCheckDateWithin7Days} info={info('runs-not-approved-near-check-date')} emphasis={data.payroll.notApprovedWithCheckDateWithin7Days > 0 ? 'attention' : undefined} />
              <Stat label="Companies with payroll runs" value={data.payroll.companiesWithRuns} />
            </dl>
            <BarList
              caption="Payroll runs by status"
              items={[
                { label: 'Draft', value: data.payroll.draft }, { label: 'Pending approval', value: data.payroll.pendingApproval },
                { label: 'Approved', value: data.payroll.approved }, { label: 'Rejected', value: data.payroll.rejected }, { label: 'Withdrawn', value: data.payroll.withdrawn },
              ]}
            />
            <p className="pc-muted">Payroll runs have no Processing, Completed or Failed state in EPN today; those are shown only where a source exists (see payments below).</p>
          </>
        )}
      </PlatformSection>

      <PlatformSection id="payments" title="Funding and payment risk" status={s.payments} info={info('payment-batches-by-status')}>
        {data.payments && (
          <>
            <dl className="pc-stats">
              <Stat label="Failed payment batches" value={data.payments.batchesFailed} emphasis={data.payments.batchesFailed > 0 ? 'critical' : undefined} />
              <Stat label="Companies with failed batches" value={data.payments.companiesWithFailedBatches} info={info('companies-with-failed-batches')} emphasis={data.payments.companiesWithFailedBatches > 0 ? 'critical' : undefined} />
              <Stat label="Awaiting settlement" value={data.payments.batchesSubmitted} />
              <Stat label="Settled" value={data.payments.batchesSettled} />
            </dl>
            <h4 className="pc-subtitle">Funding accounts{info('funding-account-verification')}</h4>
            <dl className="pc-stats">
              <Stat label="Verified" value={data.payments.fundingAccountsVerified} />
              <Stat label="Pending verification" value={data.payments.fundingAccountsPending} />
              <Stat label="Paying with no funding account" value={data.payments.companiesWithoutFundingAccount} emphasis={data.payments.companiesWithoutFundingAccount > 0 ? 'attention' : undefined} />
            </dl>
          </>
        )}
        <ul className="pc-gaps" aria-label="Payment figures EPN cannot supply">
          {['returned-direct-deposits', 'unmatched-provider-transactions'].map((id) => gap(id) && (
            <li key={id}><strong>{gap(id)!.displayName}:</strong> data unavailable. {gap(id)!.reason}</li>
          ))}
        </ul>
      </PlatformSection>

      <PlatformSection id="taxes" title="Tax payments and filings" status={null}>
        <div className="pc-split">
          <div>
            <h4 className="pc-subtitle">Tax payments{info('tax-payments-status')} {data.taxPayments?.simulated && <StatusPill kind="info">Simulated</StatusPill>}</h4>
            {data.taxPayments ? (
              <>
                <dl className="pc-stats">
                  <Stat label="Need attention (failed)" value={data.taxPayments.failedNeedsAttention} emphasis={data.taxPayments.failedNeedsAttention > 0 ? 'critical' : undefined} />
                  <Stat label="Pending" value={data.taxPayments.pendingPayments} />
                  <Stat label="Processing" value={data.taxPayments.processing} />
                  <Stat label="Total not yet paid" value={money(data.taxPayments.totalAmountDue)} />
                </dl>
                <p className="pc-muted">{data.taxPayments.simulationNote} Deposits are tracked here separately from return filings.</p>
              </>
            ) : <Unavailable reason={s.taxPayments.reason} />}
          </div>
          <div>
            <h4 className="pc-subtitle">Return filings, {data.filings?.taxYear ?? 'current year'}{info('tax-filings-status')} {data.filings?.simulated && <StatusPill kind="info">Sandbox</StatusPill>}</h4>
            {data.filings ? (
              <>
                <dl className="pc-stats">
                  <Stat label="Companies needing attention" value={data.filings.companiesNeedingAttention} emphasis={data.filings.companiesNeedingAttention > 0 ? 'critical' : undefined} />
                  <Stat label="In review" value={data.filings.companiesInReview} />
                  <Stat label="Filed" value={data.filings.companiesFiled} />
                  <Stat label="Companies that started a filing" value={data.filings.companiesWithFilings} />
                </dl>
                <p className="pc-muted">{data.filings.simulationNote}</p>
              </>
            ) : <Unavailable reason={s.filings.reason} />}
          </div>
        </div>
        <p className="pc-links">
          <Link to="/super-admin/tax-filings">Open Super Admin tax filings</Link> · <Link to="/tax-payments">Open tax payments</Link>
        </p>
      </PlatformSection>

      <PlatformSection id="subscriptions" title="Subscriptions" status={s.subscriptions} info={info('subscriptions-by-plan-and-state')}>
        {data.subscriptions && (
          <>
            <dl className="pc-stats">
              <Stat label="Past due" value={data.subscriptions.pastDue} emphasis={data.subscriptions.pastDue > 0 ? 'critical' : undefined} />
              <Stat label="Trials ending within 14 days" value={data.subscriptions.trialsExpiringWithin14Days} emphasis={data.subscriptions.trialsExpiringWithin14Days > 0 ? 'attention' : undefined} />
              <Stat label="Trials active" value={data.subscriptions.trialActive} />
              <Stat label="Active" value={data.subscriptions.active} />
              <Stat label="Trial expired" value={data.subscriptions.trialExpired} />
              <Stat label="Canceled" value={data.subscriptions.canceled} />
            </dl>
            <BarList caption="Companies by plan" items={data.subscriptions.companiesByPlan.map((p) => ({ label: p.planName, value: p.companies }))} />
            <p className="pc-unavailable-line"><strong>Monthly recurring revenue:</strong> data unavailable. {data.subscriptions.mrrUnavailableReason} Client payroll funds are never counted as Escochex revenue.</p>
          </>
        )}
      </PlatformSection>

      <PlatformSection id="health" title="Platform health" status={null} info={info('platform-service-availability')} note="Availability of each service's own basic health check, right now. Error rates, queues, jobs and webhooks have no feed in EPN today.">
        {data.health.services.length === 0 ? (
          <Unavailable reason="No services are connected to this dashboard in this environment." />
        ) : (
          <>
            <p className="pc-muted">{data.health.available} available, {data.health.unavailable} unavailable</p>
            <ul className="pc-health">
              {data.health.services.map((svc) => (
                <li key={svc.service}><span>{svc.service}</span> <StatusFromServer value={svc.status} /></li>
              ))}
            </ul>
          </>
        )}
      </PlatformSection>

      <PlatformSection id="clients" title="Client risk" status={null} info={info('client-risk-table')} note="One row per company. A status shown as Unavailable means that section could not be read, not that the company is fine. No employee, compensation or bank data is ever shown here.">
        {!data.clientRiskIncluded || !data.clientRisk ? (
          <Unavailable title="Company rows are not shown" reason={data.clientRiskUnavailableReason ?? 'Company rows need the company drill-down permission.'} />
        ) : data.clientRisk.length === 0 ? (
          <p className="pc-muted" data-testid="platform-clients-empty">No companies found.</p>
        ) : (
          <div className="pc-table-wrap">
            <table data-testid="super-admin-dashboard-company-table" className="pc-table">
              <caption className="pc-visually-hidden">Companies with their payroll, funding, payment, tax, filing and subscription status</caption>
              <thead>
                <tr>
                  <th scope="col">Company</th><th scope="col">Payroll</th><th scope="col">Next pay date</th><th scope="col">Last approved pay date</th>
                  <th scope="col">Funding</th><th scope="col">Payments</th><th scope="col">Taxes</th><th scope="col">Filing</th><th scope="col">Subscription</th><th scope="col" className="pc-num">Open alerts</th>
                </tr>
              </thead>
              <tbody>
                {data.clientRisk.map((row) => (
                  <tr key={row.companyId}>
                    <th scope="row">{row.companyName}{!row.isActive && <> <StatusPill kind="info">Inactive</StatusPill></>}</th>
                    <td><StatusFromServer value={row.payrollStatus} /></td>
                    <td>{formatDate(row.nextCheckDate)}</td>
                    <td>{formatDate(row.lastApprovedCheckDate)}</td>
                    <td><StatusFromServer value={row.fundingStatus} /></td>
                    <td><StatusFromServer value={row.paymentStatus} /></td>
                    <td><StatusFromServer value={row.taxStatus} /></td>
                    <td><StatusFromServer value={row.filingStatus} /></td>
                    <td><StatusFromServer value={row.subscriptionStatus} /></td>
                    <td className="pc-num">{row.openAlerts === null ? <span className="pc-muted">Unavailable</span> : row.openAlerts}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="pc-muted">Open alerts count runs awaiting approval, failed payment batches, tax payments and filings needing attention, and a past-due subscription. The assigned support owner is not shown: EPN does not record one.</p>
      </PlatformSection>

      <PlatformSection id="gaps" title="What this page cannot show yet" status={null}>
        <p className="pc-note">These figures have no authoritative source in EPN today, so they are left out rather than estimated.</p>
        <ul className="pc-gaps" data-testid="platform-unavailable-metrics">
          {data.unavailableMetrics.map((m) => (
            <li key={m.metricId}><strong>{m.displayName}.</strong> {m.reason}</li>
          ))}
        </ul>
      </PlatformSection>
    </>
  );
}
